import React from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, Split, Merge, Settings, Globe, Languages, Shield } from 'lucide-react';

export const FeaturesPage: React.FC = () => {
    return (
        <div className="page-container" style={{ maxWidth: '1000px' }}>
            <section className="hero">
                <h1 className="hero-title">Powerful Localization Features</h1>
                <p className="hero-subtitle">
                    Everything you need to manage Excel-based localization workflows efficiently.
                </p>
            </section>

            <div className="vertical-layout">
                {/* Feature 1: Seamless Conversion */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <FileSpreadsheet size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Excel ↔ XLIFF Conversion</h2>
                        <p>
                            Stop copy-pasting strings. PolyGrid converts your Excel files directly into industry-standard XLIFF format (1.2 or 2.1), ready for any CAT tool (Trados, memoQ, Memsource, etc.).
                        </p>
                        <ul className="feature-list">
                            <li>Preserves comments and context notes</li>
                            <li>Smart column detection</li>
                            <li>Supports multiple target languages in one sheet</li>
                        </ul>
                    </div>
                </section>

                {/* Feature 2: Smart Segmentation */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <Split size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Smart Segmentation</h2>
                        <p>
                            Ensure your translation memory stays clean. PolyGrid handles sentence splitting intelligently, or lets you define your own rules.
                        </p>
                        <ul className="feature-list">
                            <li>Built-in sentence segmentation</li>
                            <li><strong>Custom SRX</strong> support for advanced users</li>
                            <li>Respects existing line breaks and formatting</li>
                        </ul>
                    </div>
                </section>

                {/* Feature 3: Merge Workflow */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <Merge size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Seamless Merging</h2>
                        <p>
                            Once translations are done, merge them back into your original Excel file with a single click. No manual alignment required.
                        </p>
                        <ul className="feature-list">
                            <li>Combines multiple XLIFF files</li>
                            <li>Updates target columns automatically</li>
                            <li>Preserves original Excel formatting and formulas</li>
                        </ul>
                    </div>
                </section>

                {/* Feature 4: Developer Friendly */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <Settings size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Developer Ready</h2>
                        <p>
                            Need to automate? PolyGrid is built with automation in mind.
                        </p>
                        <ul className="feature-list">
                            <li>JSON/YAML configuration export</li>
                            <li>Consistent ID generation</li>
                        </ul>
                    </div>
                </section>

                {/* Feature 5: Global Support */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <Globe size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Native Language Support</h2>
                        <p>
                            We support over 100 languages with native name detection.
                        </p>
                        <ul className="feature-list">
                            <li>Auto-detects languages from Excel headers</li>
                            <li>Supports RTL languages (Arabic, Hebrew)</li>
                            <li>Locale-aware processing</li>
                        </ul>
                    </div>
                </section>

                {/* Feature 8: Security & Privacy */}
                <section className="card feature-card">
                    <div className="feature-icon">
                        <Shield size={32} color="var(--primary)" />
                    </div>
                    <div className="feature-content">
                        <h2>Security & Privacy First</h2>
                        <p>
                            Your data stays yours. We've built PolyGrid with privacy at its core.
                        </p>
                        <ul className="feature-list">
                            <li><strong>No file storage</strong> — files are processed in memory and immediately discarded</li>
                            <li>All transfers encrypted via HTTPS/TLS</li>
                            <li>No third-party tracking or analytics on your content</li>
                        </ul>
                    </div>
                </section>
            </div>

            <div className="cta-section" style={{ textAlign: 'center', marginTop: '4rem' }}>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <Link to="/" className="button primary large">
                        Start Converting Now
                    </Link>
                    <Link to="/pricing" className="button secondary large">
                        View Pricing
                    </Link>
                </div>
            </div>
        </div>
    );
};
